/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-props-no-spreading */
import React, {useEffect, useState, useReducer, useContext, useCallback} from 'react';
import {Spinner} from 'reactstrap';
import {useLocation} from 'react-router-dom';
import Toolbar from './Toolbar';
import EditableGrid from './EditableGrid';
import PaginationFooter from './PaginationFooter';
import {DataGridContainer, AccumulatorCel, AccumulatorRow} from './styled';
import {
  normalizeRowData,
  generateRowRule,
  generateHeader,
  refreshBlur,
  isObject,
  getKeyValue,
  isPartialDate,
  formatToEditType
} from '../../utils/utils';
import {useInitialRenderEffect} from '../../utils/functions';
import Alert from '../Notification';
import ConfirmDialog from '../Dialog/ConfirmDialog';
import Accumulators from './Accumulators';
import dataGridReducer from './dataGridReducer';
import {DATAGRID, ROLES} from '../../utils/constants';
import DataGridContext from './DataGridContext';
import {DataContext} from '../../utils/DataProvider';
import {setMode, setCurrentLine, setCurrentCel, popTableActionQueue} from './dataGridActions';
import secureStorage from '../../utils/SecureStorage';

const DataGrid = ({
  header,
  data = [],
  groupBy,
  keyProp,
  keyValueProp,
  titleProp,
  maxHeight,
  onChange = () => {},
  fixedColumn,
  width,
  fixedAccumulator = false,
  totalItems = 0,
  editableCelOnly = false,
  actions = null,
  validateCallback = () => {},
  accumulators = null,
  disabledPaginate,
  currentPage: externalCurrentPage,
  itemsPerPage: externalItemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  sort,
  onSortChange,
}) => {
  const [state, dispatch] = useReducer(dataGridReducer, {
    mode: DATAGRID.TABLE.MODE.READ,
    currentLine: null,
    filters: [],
    initialData: data,
    actionQueue: [],
    fixedAccumulator,
    errors: {},
  });

  const location = useLocation();

  const perPageStorage = () => {
    const perPageSession = JSON.parse(window.localStorage.getItem('perPageStorage')) || {};
    if (perPageSession[location.pathname]) {
      return perPageSession[location.pathname];
    }
    return 50;
  };

  const {setFilterParams} = useContext(DataContext);

  const [alertProps, setAlertProps] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [internalItemsPerpage, setInternalItemsPerpage] = useState(perPageStorage());
  const [internalCurrentPage, setInternalCurrentPage] = useState(0);
  const [internalSort, setInternalSort] = useState({field: 'id', order: 'desc'});
  const [editAction, setEditAction] = useState(false);
  const [loading, setLoading] = useState(false);
  const [headerTemplate, setHeaderTemplate] = useState([]);
  const [fieldTemplate, setFieldTemplate] = useState([]);
  const [columnsTemplate, setColumnTemplate] = useState('');
  const [cel, setCel] = useState(null);

  const itemsPerpage = externalItemsPerPage ?? internalItemsPerpage;
  const currentPage = externalCurrentPage ?? internalCurrentPage;
  const currentSort = sort ?? internalSort;

  const handleItemsPerPageChange = onItemsPerPageChange ?? setInternalItemsPerpage;
  const handlePageChange = onPageChange ?? setInternalCurrentPage;
  const handleSortChange = onSortChange ?? setInternalSort;

  const ALLOWED_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TECHNICAL];

  const translateErros = (error) => {
    if (error.indexOf('temporaryPassword') > 0) return 'Senha inválida: mínimo de 8 caracteres';
    if (error.indexOf('email already exists') > 0) return 'Já existe um usuário com este email';
    if (!error) return 'Erro ao salvar dados!';
    if (!error.indexOf('Não foi possível remover users') > 0) return error;
    if (error.indexOf('Não foi possível remover usuário. Existe(m) registro(s) vinculado(s) em cliente.') > 0) return error;
    return 'Preencha todos os campos';
  };

  const onChangeSubmit = useCallback(async (functionChange, responseData, msg) => {
    setLoading(true);
    const resp = await functionChange(responseData, state.currentLine, cel);
    setAlertProps(null);

    if (resp && (resp.status === 200 || resp.status === 201)) {
      setAlertProps({type: 'success', message: msg, time: 5000});
    } else if (resp?.response?.data?.message) {
      setAlertProps({type: 'error', message: translateErros(resp.response.data.message), time: 5000});
    }

    setLoading(false);
  }, [cel, state.currentLine]);

  const deleteRow = async () => {
    const dataToRemove = data.find((item) => item.id === state.currentLine);
    await onChangeSubmit(confirmDialog.onClick, dataToRemove, 'Excluído com sucesso');
    dispatch(setMode(DATAGRID.TABLE.MODE.READ));
    dispatch(setCurrentLine(null));
  };

  const actionTrigger = useCallback(async (btn) => {
    switch (btn.action) {
      case 'add':
        dispatch(setMode(DATAGRID.TABLE.MODE.NEW_LINE));
        dispatch(setCurrentLine(null));
        break;
      case 'edit':
        dispatch(setMode(DATAGRID.TABLE.MODE.EDIT));
        break;
      case 'cancel':
        dispatch(setMode(DATAGRID.TABLE.MODE.READ));
        dispatch(setCurrentLine(null));
        break;
      case 'save': {
        if (state.errors && Object.keys(state.errors).length) return;

        const validation = refreshBlur(state.mode === 'NEW_LINE' ? -1 : state.currentLine);

        if (validation) {
          const responseData = normalizeRowData(state.data, headerTemplate, data);
          delete responseData.changed;

          await onChangeSubmit(
            onChange,
            responseData,
            state.mode === DATAGRID.TABLE.MODE.NEW_LINE
              ? 'Sucesso ao inserir novos dados'
              : 'Editado com sucesso!'
          );

          dispatch(setMode(DATAGRID.TABLE.MODE.READ));
          dispatch(setCurrentLine(null));
        }
        break;
      }
      case 'remove':
        setConfirmDialog(btn);
        break;
      default:
        if (btn.onClick) {
          btn.onClick(btn);
        }
        break;
    }
  }, [
    dispatch,
    headerTemplate,
    onChange,
    onChangeSubmit,
    state.data,
    state.errors,
    state.mode,
    state.currentLine,
    data
  ]);

  useInitialRenderEffect(() => {
    const removeRestrictedFields = () => {
      const user = JSON.parse(secureStorage.getItem('user'));
      const hasAccess = ALLOWED_ROLES.includes(user.user_type);

      if (!hasAccess) {
        header = header.filter((item) => item.field !== 'validated');
      }
    };

    const makeTemplateHeader = () => {
      const {newFieldTemplate, newHeaderTemplate, newWidthCollection} = generateHeader(header, groupBy);
      setFieldTemplate(newFieldTemplate);
      setHeaderTemplate(newHeaderTemplate);
      setColumnTemplate(generateRowRule(newWidthCollection));
    };

    removeRestrictedFields();
    makeTemplateHeader();
  }, []);

  useEffect(() => {
    dispatch(
      setCurrentCel(
        state.currentCel
          ? Object.assign(state.currentCel, editableCelOnly)
          : {editableCelOnly}
      )
    );
  }, [itemsPerpage, currentPage, currentSort, fieldTemplate, editableCelOnly, state.currentCel, dispatch]);

  useEffect(() => {
    state.actionQueue.forEach((action) => {
      actionTrigger({action, validate: true});
      dispatch(popTableActionQueue());
    });
  }, [state.actionQueue, actionTrigger, dispatch]);

  useEffect(() => {
    const generateFilter = () => {
      if (!Object.keys(state.filters)?.length) return {};

      return Object.keys(state.filters).reduce((acc, key) => {
        const fieldFilterParams = state.filters[key];
        const partialdate = fieldFilterParams.type === 'date'
          ? isPartialDate(fieldFilterParams.value1 || fieldFilterParams.value2)
          : '';

        const hasFirstOperatorFilled = !!fieldFilterParams.operation1;
        const hasSecondOperatorFilled = !!fieldFilterParams.operation2;

        let filterReturn = {};

        if ((hasFirstOperatorFilled && !hasSecondOperatorFilled) || (!hasFirstOperatorFilled && hasSecondOperatorFilled)) {
          const operator = hasFirstOperatorFilled ? fieldFilterParams.operation1.value : fieldFilterParams.operation2.value;
          const value = hasFirstOperatorFilled ? fieldFilterParams.value1 : fieldFilterParams.value2;

          filterReturn = {
            ...acc,
            [`wherecustom[${getKeyValue(key, fieldFilterParams.originalKey)}${partialdate ? '$partialdate' : ''}:null]`]:
              `${formatToEditType(value, fieldFilterParams.type, operator)};${operator}`,
          };
        } else if (hasFirstOperatorFilled && hasSecondOperatorFilled) {
          const operator1 = fieldFilterParams.operation1.value;
          const {value1} = fieldFilterParams;
          const joinOperator = fieldFilterParams.joinOperation.value;
          const operator2 = fieldFilterParams.operation2.value;
          const {value2} = fieldFilterParams;

          filterReturn = {
            ...acc,
            [`wherecustom[${key}${isObject(state.initialData?.[0]?.[key])}${partialdate ? '$partialdate' : ''}:${joinOperator}]`]:
              `${formatToEditType(value1, fieldFilterParams.type, operator1)};${operator1},${formatToEditType(value2, fieldFilterParams.type, operator2)};${operator2}`,
          };
        }

        if (fieldFilterParams.checkboxList && fieldFilterParams.checkboxList.length) {
          const checkboxStrings = fieldFilterParams.checkboxList.map((c) => `'${c}'`).join(', ');

          filterReturn = {
            ...filterReturn,
            ...(headerTemplate.find((item) => item.key === key)?.editType === 'datepicker'
              ? {[`wherenotin[${key}$partialdate]`]: checkboxStrings}
              : {[`wherein[${key}]`]: checkboxStrings}),
          };
        }

        return {...acc, ...filterReturn};
      }, {});
    };

    setFilterParams(generateFilter());
  }, [state.filters, headerTemplate, setFilterParams, state.initialData]);

  useEffect(() => {
    const handleKeyUp = (event) => {
      if (event.code === 'Delete') {
        setTimeout(() => {
          const removeBtn = document.getElementById('remove');
          if (removeBtn) {
            removeBtn.click();
          }
        }, 300);
      }
    };

    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return (
    <DataGridContext.Provider value={{state, dispatch}}>
      <Toolbar editAction={editAction} onClick={actionTrigger} customConfiguration={actions} />

      <DataGridContainer className="datagrid-general" style={{minHeight: '200px', maxHeight: maxHeight || '350px'}}>
        {loading || !fieldTemplate.length ? (
          <div className="d-flex w-100 h-100 align-items-center justify-content-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className="content-grid d-flex full-screen">
            {fixedColumn && (
              <div className="fixed-column col-3 p-0 d-flex flex-column justify-content-end">
                <EditableGrid
                  width={width || 'auto'}
                  accumulators={accumulators}
                  validateCallback={validateCallback}
                  groupBy={groupBy}
                  headerTemplate={fixedColumn.header}
                  fieldTemplate={fixedColumn.header}
                  columnsTemplate="repeat(1, 7fr)"
                  keyProp={keyProp}
                  keyValueProp={keyValueProp}
                  titleProp={titleProp}
                  setCel={setCel}
                  setSort={handleSortChange}
                  dataValues={fixedColumn.data}
                  gridListener={(dataFromRow) => dataFromRow?.changed && setEditAction(true)}
                />
                {fixedColumn.accumulator && fixedColumn.accumulator.length > 0 && (
                  <>
                    {fixedColumn.accumulator.map((item) => (
                      <AccumulatorRow key={item.title}>
                        <AccumulatorCel>
                          <div style={{marginRight: '5px'}}>{item.title}:</div>
                        </AccumulatorCel>
                      </AccumulatorRow>
                    ))}
                  </>
                )}
              </div>
            )}

            <div className={`editable-grid ${fixedColumn ? 'col-9 p-0 overflow-auto' : 'w-100'}`}>
              <div className="content-grid-scroll">
                <EditableGrid
                  width={`${fixedColumn ? 'max-content' : `${groupBy ? 'auto' : width}`}`}
                  accumulators={accumulators}
                  validateCallback={validateCallback}
                  groupBy={groupBy}
                  headerTemplate={headerTemplate}
                  fieldTemplate={fieldTemplate}
                  columnsTemplate={columnsTemplate}
                  keyProp={keyProp}
                  keyValueProp={keyValueProp}
                  titleProp={titleProp}
                  setSort={handleSortChange}
                  dataValues={data}
                  setCel={setCel}
                />
                {accumulators && fixedColumn && (
                  <Accumulators
                    fixed={fixedAccumulator}
                    groupBy={groupBy}
                    groupedData={data}
                    accumulatorsConfig={accumulators}
                    fieldHeaders={fieldTemplate.filter((_header) => !_header.subFields)}
                    columnsTemplate={columnsTemplate}
                    width={fixedColumn ? 'max-content' : width}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {accumulators && !fixedColumn && (
          <Accumulators
            customClass="sticky"
            fixed={fixedAccumulator}
            groupBy={groupBy}
            groupedData={data}
            general
            accumulatorsConfig={accumulators}
            fieldHeaders={fieldTemplate.filter((_header) => !_header.subFields)}
            columnsTemplate={columnsTemplate}
            width={fixedColumn ? 'max-content' : width}
          />
        )}
      </DataGridContainer>

      {(disabledPaginate === false || disabledPaginate === undefined) && (
          <PaginationFooter
            activePage={currentPage}
            itemsCountPerPage={itemsPerpage}
            totalItemsCount={totalItems}
            pageRangeDisplayed={1}
            itemClass="page-item"
            linkClass="page-link"
            onChange={(paginationData) => {
              if (paginationData?.itemsPerPage) {
                handleItemsPerPageChange(paginationData.itemsPerPage);
                handlePageChange(0);
              } else if (paginationData?.pageNumber) {
                handlePageChange(paginationData.pageNumber - 1);
              }
            }}
          />
        )}

      {alertProps && <Alert type={alertProps.type} message={alertProps.message} time={alertProps.time} />}

      {confirmDialog && (
        <ConfirmDialog
          functionAction={deleteRow}
          setConfirmDialog={setConfirmDialog}
          message="Tem certeza que deseja excluir esse registro?"
        />
      )}
    </DataGridContext.Provider>
  );
};

export default DataGrid;